import { ChangeDetectionStrategy, Component, computed, inject, OnInit, Signal } from '@angular/core';
import { NAVIGATION_TOKEN, NavigationProvider } from '@core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable, take, tap } from 'rxjs';
import { UpdateTeamCommandHandler } from '../../application/handlers';
import { PageActionsComponent, PageLayoutComponent, TitileNavbarComponent } from '@ui-components';
import { ActivatedRoute, Params } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-edit-team',
  templateUrl: './edit-team.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TitileNavbarComponent, ReactiveFormsModule, PageLayoutComponent, PageActionsComponent],
  providers: [UpdateTeamCommandHandler]
})
export class EditTeamComponent implements OnInit {
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly navigationProvider: NavigationProvider = inject(NAVIGATION_TOKEN);
  private readonly updateTeamCommandHandler: UpdateTeamCommandHandler = inject(UpdateTeamCommandHandler);

  readonly teamNameForm: FormGroup = new FormGroup({
    id: new FormControl(),
    teamName: new FormControl('', [Validators.required, Validators.maxLength(25)])
  });

  private readonly teamNameValue: Signal<string | undefined> = toSignal(
    this.teamNameForm.get('teamName')!.valueChanges as Observable<string>
  );

  readonly isNameOverAvailableLength: Signal<boolean> = computed(() => {
    const name: string | undefined = this.teamNameValue();
    return !!name && name.length > 25;
  });

  ngOnInit(): void {
    this.initFormFromRoute();
  }

  isFieldInvalid(fieldName: string): boolean {
    const control: AbstractControl<unknown, unknown> | null = this.teamNameForm.get(fieldName);
    return !!control?.touched && !!control?.invalid;
  }

  saveData(): void {
    const id: string = this.teamNameForm?.value?.id;
    const teamName: string = this.teamNameForm?.value?.teamName;

    this.updateTeamCommandHandler
      .update(id, { teamName })
      .pipe(take(1))
      .subscribe(() => this.navigationProvider.back());
  }

  private initFormFromRoute(): void {
    this.activatedRoute.params
      .pipe(
        take(1),
        tap((params: Params) =>
          this.teamNameForm.patchValue({
            id: params['id'],
            teamName: params['teamName']
          })
        )
      )
      .subscribe();
  }
}
